/* eslint-disable camelcase */

import jsen from 'jsen';
import {Dictionary, fromPairs, keyBy, last} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useUnmountCancelToken} from '../../../srcCommon/helpers/hookHelpers';
import {CancelToken} from '../../helpers/asyncCancelHelpers';
import {makeAsyncDataWithRetry} from '../../helpers/asyncDataHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ApiIntegrations, IntegrationModel, IntegrationRuleModel} from '../../models/api/integrationModel';
import {
  ApiIntegrationTypesWithSchema,
  CommonSchema,
  IntegrationSchemaTypesEnum,
  IntegrationTypesWithSchemaModel,
} from '../../models/api/integrationTypesModel';
import {fetchApiAsync} from '../../services/http/http';
import {getCompany} from '../../store/data/company/companySelectors';
import {Parameter, ParameterId, SchemaReference} from '../editors/commons/parameterModel';
import {RuleAction} from './ruleActionModel';
import {RuleCondition} from './ruleConditionModel';
import {injectExpressionSchema} from './ruleIntegrationHelpers';
import {
  IntegrationId,
  makeIntegrationId,
  makeRuleActionId,
  makeRuleConditionId,
  RuleActionDefinition,
  RuleConditionDefinition,
  ValidateFunction,
} from './ruleParameterModel';

const {isSchemaRef} = Parameter;

// @RULES TODO: ruleValidatorOptions
// const ruleValidatorOptions = {
//   formats: {
//     teammate: function (value) {
//       return _.isFinite(value) && team.activeAll.length > 0;
//     },
//     inbox: function (value) {
//       return _.isFinite(value) && inboxes.participating.length > 0;
//     },
//     channel: function (value) {
//       return _.isFinite(value) && channels.listAdministrating().length > 0;
//     },
//     tag: function (value) {
//       return _.isFinite(value);
//     },
//     response: function (value) {
//       return _.isFinite(value) && answers.all.length > 0;
//     },
//     group: function (value) {
//       return _.isFinite(value) && cardGroups.all.length > 0;
//     },
//   };
// };

/*
 * Constants.
 */

const integrationTypesUrl = '/api/1/integration_types';
const defaultIntegrationId = 0;

/*
 * Types.
 */

export interface IntegrationSchema {
  type: IntegrationSchemaTypesEnum;
  default: Record<string, any>;
  properties: Record<ParameterId, Parameter> | undefined;
  required: IntegrationRuleModel['required'];
  definitions: IntegrationTypesWithSchemaModel['commonSchema'];
  $ref?: string;
}

interface SimpleAction extends Pick<RuleAction, 'type' | 'integration_id'> {}
export interface SimpleCondition extends Pick<RuleCondition, 'type' | 'integration_id'> {}

type DefinitionMapKey = `${IntegrationId}-${RuleConditionDefinition['id'] | RuleActionDefinition['id']}`;

/*
 * Class.
 */

export class RuleIntegration {
  integrations: ReadonlyArray<IntegrationModel>;
  integrationTypes: IntegrationTypesWithSchemaModel;

  private ruleConditionDefinitions: ReadonlyArray<RuleConditionDefinition> = [];
  private ruleActionDefinitions: ReadonlyArray<RuleActionDefinition> = [];

  ruleConditionIdMap: Dictionary<RuleConditionDefinition>;
  ruleActionIdMap: Dictionary<RuleActionDefinition>;

  private ruleConditionDefinitionsMap = new Map<DefinitionMapKey, RuleConditionDefinition>();
  private ruleActionDefinitionsMap = new Map<DefinitionMapKey, RuleActionDefinition>();

  constructor(
    integrations: ReadonlyArray<IntegrationModel>,
    integrationTypes: IntegrationTypesWithSchemaModel,
  ) {
    this.integrations = integrations;
    this.integrationTypes = integrationTypes;

    this.getRuleConditionDefinitions();
    this.ruleConditionIdMap = keyBy(this.ruleConditionDefinitions, 'id');

    this.getRuleActionDefinitions();
    this.ruleActionIdMap = keyBy(this.ruleActionDefinitions, 'id');
  }

  static listIntegrations(companyUrl: string, cancelToken?: CancelToken) {
    return fetchApiAsync(ApiIntegrations, `${companyUrl}/integrations`, {cancelToken});
  }

  getIntegrationByName(name: string) {
    return this.integrations.find((integration) => integration.name === name);
  }

  static listIntegrationTypes(cancelToken?: CancelToken) {
    return fetchApiAsync(ApiIntegrationTypesWithSchema, integrationTypesUrl, {cancelToken});
  }

  private static isDefaultIntegration(definition: RuleConditionDefinition | RuleActionDefinition) {
    return definition.integration_id === defaultIntegrationId;
  }

  private static isThirdPartyIntegration(definition: RuleConditionDefinition | RuleActionDefinition) {
    return !RuleIntegration.isDefaultIntegration(definition);
  }

  getThirdPartyActions(): ReadonlyArray<RuleActionDefinition> {
    return this.ruleActionDefinitions.filter(RuleIntegration.isThirdPartyIntegration);
  }

  resolveRawSchemaReference(ref: SchemaReference) {
    const refName = last(ref.split('/'));
    return isDefined(refName) ? this.integrationTypes.commonSchema[refName] : undefined;
  }

  private resolveDefinitionParams(
    params: Record<string, any> | undefined,
  ): Partial<Record<ParameterId, CommonSchema>> {
    if (!params) {
      return {};
    }

    return Object.entries(params).reduce((acc, [parameterId, parameter]: [ParameterId, Parameter]) => {
      if (isSchemaRef(parameter)) {
        return {
          ...acc,
          [parameterId]: this.resolveRawSchemaReference(parameter.$ref),
        };
      }

      return acc;
    }, {});
  }

  private getRuleConditionDefinitions(): ReadonlyArray<RuleConditionDefinition> {
    // If we already loaded definitions, return them.
    if (this.ruleConditionDefinitions.length > 0) {
      return this.ruleConditionDefinitions;
    }

    this.ruleConditionDefinitions = this.integrations
      .filter((integration) => integration.isEnabled)
      .flatMap((integration) => {
        if (!integration.rules?.conditions) {
          return undefined;
        }

        return Object.entries(integration.rules.conditions).map(
          ([conditionTypeId, conditionType]: [string, IntegrationRuleModel]) => {
            const {name, altName, tooltip, help, params, required, restricted} = conditionType;

            const schema: IntegrationSchema = {
              type: IntegrationSchemaTypesEnum.OBJECT,
              default: {},
              properties: makePropertiesFromRuleIntegration(params),
              required,
              definitions: this.integrationTypes.commonSchema,
            };

            const validator = jsen(schema, {});
            const defaultParams = validator.build({});

            const validate: ValidateFunction = (p) => {
              validator(p);
              return validator.errors;
            };

            return {
              id: makeRuleConditionId(conditionTypeId),
              name,
              altName,
              tooltip,
              integration_id: makeIntegrationId(integration.id),
              parameters: params ?? ({} as Record<ParameterId, Parameter>),
              resolvedParameters: this.resolveDefinitionParams(params),
              defaultParams,
              validate,
              required,
              restricted,
              help,
            };
          },
        );
      })
      .filter(isDefined);

    return this.ruleConditionDefinitions;
  }

  private getRuleActionDefinitions(): ReadonlyArray<RuleActionDefinition> {
    // If we already loaded definitions, return them.
    if (this.ruleActionDefinitions.length > 0) {
      return this.ruleActionDefinitions;
    }

    this.ruleActionDefinitions = this.integrations
      .filter((integration) => integration.isEnabled)
      .flatMap((integration) => {
        if (!integration.rules?.actions) {
          return undefined;
        }

        return Object.entries(integration.rules.actions).map(
          ([actionTypeId, actionType]: [string, IntegrationRuleModel]) => {
            const {name, altName, tooltip, help, params, required, restricted} = actionType;

            const schema: IntegrationSchema = {
              type: IntegrationSchemaTypesEnum.OBJECT,
              default: {},
              properties: makePropertiesFromRuleIntegration(actionType.params),
              required: actionType.required,
              definitions: this.integrationTypes.commonSchema,
            };

            const validator = jsen(schema, {});
            const defaultParams = validator.build({});

            const validate: ValidateFunction = (p) => {
              validator(p);
              return validator.errors;
            };

            return {
              id: makeRuleActionId(actionTypeId),
              name,
              altName,
              tooltip,
              integration_id: makeIntegrationId(integration.id),
              parameters: params ?? ({} as Record<ParameterId, Parameter>),
              resolvedParameters: this.resolveDefinitionParams(params),
              defaultParams,
              validate,
              required,
              restricted,
              help,
            };
          },
        );
      })
      .filter(isDefined);

    return this.ruleActionDefinitions;
  }

  findConditionDefinition(condition: SimpleCondition) {
    const key = makeKey(condition);
    if (this.ruleConditionDefinitionsMap.has(key)) {
      return this.ruleConditionDefinitionsMap.get(key);
    }

    const conditionDefinition = this.getRuleConditionDefinitions().find(
      (c) => c.id === condition.type && c.integration_id === condition.integration_id,
    );

    if (conditionDefinition) {
      this.ruleConditionDefinitionsMap.set(key, conditionDefinition);
    }

    return conditionDefinition;
  }

  findActionDefinition(action: SimpleAction) {
    const key = makeKey(action);
    if (this.ruleActionDefinitionsMap.has(key)) {
      return this.ruleActionDefinitionsMap.get(key);
    }

    const actionDefinition = this.getRuleActionDefinitions().find(
      (c) => c.id === action.type && c.integration_id === action.integration_id,
    );

    if (actionDefinition) {
      this.ruleActionDefinitionsMap.set(key, actionDefinition);
    }

    return actionDefinition;
  }
}

/*
 * Helpers.
 */

function makeKey(value: SimpleAction | SimpleCondition): DefinitionMapKey {
  return `${value.integration_id}-${value.type}`;
}

function makePropertiesFromRuleIntegration(
  params: IntegrationRuleModel['params'],
): Record<ParameterId, Parameter> | undefined {
  if (!params) {
    return undefined;
  }

  return fromPairs(Object.entries(params).map(([key, value]) => [key, injectExpressionSchema(value)]));
}

const API_ERROR_MESSAGE = `An error occurred while fetching integrations`;

export function useRuleIntegrations() {
  const company = useAppSelector(getCompany);
  const cancelToken = useUnmountCancelToken();
  const [error, setError] = useState<Error | null>(null);
  const [integrations, setIntegrations] = useState<ReadonlyArray<IntegrationModel>>();
  const [integrationTypes, setIntegrationTypes] = useState<IntegrationTypesWithSchemaModel>();

  const fetchData = useCallback(() => {
    setError(null);

    RuleIntegration.listIntegrations(company.url, cancelToken).then(setIntegrations, (err) =>
      setError(new ExtendedError(API_ERROR_MESSAGE, err)),
    );

    RuleIntegration.listIntegrationTypes(cancelToken).then(setIntegrationTypes, (err) =>
      setError(new ExtendedError(API_ERROR_MESSAGE, err)),
    );
  }, [company, cancelToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const ruleIntegrations = useMemo(() => {
    if (integrations && integrationTypes) {
      return new RuleIntegration(integrations, integrationTypes);
    }

    return null;
  }, [integrations, integrationTypes]);

  return makeAsyncDataWithRetry(ruleIntegrations, error, fetchData);
}
